import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "material-symbols";
import "../../../../../src/assets/stylesheets/ClassroomSecondaryNav.scss";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MEDIA_QUERY } from "../../../../../src/constants/mediaQueryBreakpoints";
import FocusTrap from "focus-trap-react";
import { getUserRoles } from "../../../../../src/utils/userRoleHelper";
import useSchool from "../../../../../src/hooks/useSchool";
import { hasLoaded } from "../../../../../src/utils/loadingHelpers";
import { Button } from "@raspberrypifoundation/design-system-react";
import { BLOCKS_PATH } from "../AppRoutes/ClassroomAppRoutes";

const ClassroomSecondaryNav = () => {
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, "");
  const isMobile = useMediaQuery({ query: MOBILE_MEDIA_QUERY });
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const school = useSelector((state) => state.school);
  const userRoles = getUserRoles(school);
  useSchool({ accessToken: auth.user?.access_token });

  const showSecondaryNav = location.pathname.match(
    new RegExp(`^/[a-z]{2}(-[A-Z]{2})?(/${BLOCKS_PATH})?/?$`),
  );

  const toggleMenu = () => setMenuIsOpen(!menuIsOpen);
  const closeMenu = () => setMenuIsOpen(false);

  const homePath = `/${locale}`;
  const blocksEditorPath = `/${locale}/${BLOCKS_PATH}`;
  const schoolPath = `/${locale}/school`;

  const homeClasses = classNames("classroom-secondary-nav__link", {
    "classroom-secondary-nav__link--active": currentPath === homePath,
  });
  const blocksEditorClasses = classNames("classroom-secondary-nav__link", {
    "classroom-secondary-nav__link--active": currentPath === blocksEditorPath,
  });
  const schoolClasses = classNames("classroom-secondary-nav__link", {
    "classroom-secondary-nav__link--active": currentPath === schoolPath,
  });

  const linksClasses = classNames("classroom-secondary-nav__links", {
    "classroom-secondary-nav__links--mobile": isMobile,
  });

  useEffect(() => {
    const appElement = document.querySelector("#app");

    if (isMobile && menuIsOpen && appElement) {
      appElement.style.overflow = "hidden";
    } else if (appElement) {
      appElement.style.overflow = "auto";
    }
  }, [isMobile, menuIsOpen]);

  //  If the escape key is pressed close the menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  if (!showSecondaryNav) {
    return;
  }

  return (
    <div className="classroom-secondary-nav">
      <span className="classroom-secondary-nav__title">
        {t("classroomSecondaryNav.title")}
      </span>
      <FocusTrap active={isMobile && menuIsOpen}>
        <div
          className={
            isMobile ? "classroom-secondary-nav__links-wrapper--mobile" : ""
          }
        >
          {isMobile && (
            <Button
              type="tertiary"
              icon={menuIsOpen ? "close" : "menu"}
              onClick={toggleMenu}
            />
          )}
          {(!isMobile || menuIsOpen) && (
            <div className={linksClasses}>
              <Link
                className={homeClasses}
                to={`/${locale}`}
                onClick={closeMenu}
                aria-current={currentPath === homePath ? "page" : undefined}
              >
                <span className="classroom-secondary-nav__link-text">
                  {t("classroomSecondaryNav.home")}
                </span>
              </Link>
              <Link
                className={blocksEditorClasses}
                to={`/${locale}/${BLOCKS_PATH}`}
                onClick={closeMenu}
                aria-current={
                  currentPath === blocksEditorPath ? "page" : undefined
                }
              >
                <span className="classroom-secondary-nav__link-text">
                  {t("classroomSecondaryNav.blocksEditor")}
                </span>
              </Link>
              {auth.user && hasLoaded(school?.loading) && (
                <>
                  <hr
                    className="classroom-secondary-nav__separator"
                    aria-hidden="true"
                  />
                  {!!userRoles.length && (
                    <Link
                      className={schoolClasses}
                      to={`/${locale}/school`}
                      onClick={closeMenu}
                      aria-current={
                        currentPath === schoolPath ? "page" : undefined
                      }
                    >
                      <span className="classroom-secondary-nav__link-text">
                        {t("classroomSecondaryNav.school")}
                      </span>
                    </Link>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </FocusTrap>
    </div>
  );
};

export default ClassroomSecondaryNav;
